import axios from 'axios'
import { get } from 'svelte/store'
import { find } from 'lodash'
import { user, user_font_tags, user_saved_selections, user_subscription, 
  detected_fonts, server_path, client_path, user_valid, new_subscription_id, modals,
  yearly_upsell_from_pro_to_business_estimate, monthly_upsell_from_pro_to_business_estimate, 
  can_upgrade_from_pro_to_business, env, subscription_email_mismatch,
  business_subscription_deactivated, pro_subscription_deactivated, 
  business_subscription_active
} from '../stores'
import { getFontClassificationsFromDB } from './fonts'
import { loadStorefronts } from './storefronts'
import { getUserTaggedFonts, mapUserTagsToFonts } from './tags'
import { getSubscriptionChangeEstimate } from './subscriptions'
import { loadAndMapUploadedFonts } from './font_files'

// Log in the user and set user data
export async function logInUser(form_data) {
  try {
    // Try to login and get user data
    let user_response = form_data ? await loginUserFromForm(form_data) : await loginUserFromCookie()
    
    // Check if we got a valid response
    if (!user_response || !user_response.data) {
      console.log('User - no valid response from login')
      return false
    }
    
    let user_data = user_response.data

    // Exit if cannot log in
    if (!user_data.id) { console.log('User - could not log in user'); return false; }
    
    // continue if can log in
    console.log('User - logged in')
    
    // keep user logged in by resetting the session every 20 minutes
    window.setInterval(() => loginUserFromCookie(), 1000*60*20 )

    // Set up user integrations
    setupUserIntegrations(user_data, get(env))

    // get/set user_subscription and set user_valid
    try {
      await runUserDataRoutine(user_data)
    } catch (error) {
      console.error('Error running user data routine:', error)
    }

    // load and map uploaded fonts
    try {
      await loadAndMapUploadedFonts()
    } catch (error) {
      console.error('Error loading and mapping uploaded fonts:', error)
    }

    // Get and set saved storefronts
    try {
      await loadStorefronts()
    } catch (error) {
      console.error('Error loading storefronts:', error)
    }

    // Get and set tagged fonts if user is valid
    try {
      let tags_response = await getUserTaggedFonts()
      if (tags_response && tags_response.data) {
        user_font_tags.set(tags_response.data)
        await mapUserTagsToFonts(tags_response.data)
      }
    } catch (error) {
      console.error('Error getting user tagged fonts:', error)
    }

    // Get and set saved selections
    try {
      let selections_response = await getUserSavedSelections()
      if (selections_response && selections_response.data) {
        user_saved_selections.set(selections_response.data)
        await mapUserSelectionsToFonts(selections_response.data)
      }
    } catch (error) {
      console.error('Error getting user saved selections:', error)
    }

    // Get and set font classifications
    try {
      getFontClassificationsFromDB(user_data)
    } catch (error) {
      console.error('Error getting font classifications:', error)
    }
    // Get and set user account management link and upsell estimate
    try {
      await getUserSubscriptionRelatedData()
    } catch (error) {
      console.error('Error getting user subscription related data:', error) 
    }
    
    return {
      user: user_data
    }
  } catch (error) {
    console.error('Error logging in user:', error)
    return false
  }
}

// Log out the user and optionally refresh the page
export function logOutUser(refresh = false, login_email = null) {
  axios.get(get(server_path) + 'user/logout.php', {withCredentials: true})
  .then(() => {
    if (refresh) window.location.href = get(client_path) + (login_email ? '?login_email=' + login_email : '')
  }, (error) => {
    console.log("Error while logging out");
    console.log(error)
    // Still redirect if refresh was requested, even if logout failed
    if (refresh) window.location.href = get(client_path) + (login_email ? '?login_email=' + login_email : '')
  })
}

// Try to log in the user from a cookie
function loginUserFromCookie() {
  console.log('User - trying to log user in from cookie')
  return axios.get(get(server_path) + 'user/check_if_logged_in.php', {withCredentials: true})
    .catch(error => {
      console.error('Error logging in from cookie:', error)
      return { data: null }
    })
}

// Try to log in the user from a form
function loginUserFromForm(form_data) {
  console.log('User - trying to log user in from form')
  return axios.post(get(server_path) + 'user/login.php', form_data, {withCredentials: true})
    .catch(error => {
      console.error('Error logging in from form:', error)
      return { data: null }
    })
}

// Get user saved selections from the server
function getUserSavedSelections() {
  console.log('User - getting saved selections')
  return axios.get(get(server_path) + 'selections/get.php', { withCredentials: true })
    .catch(error => {
      console.error('Error getting saved selections:', error)
      return { data: null }
    })
}

// Get user subscription details from the server
export function getUserSubscription(s_id) {
  console.log('User - getting subscription details')
  if (!s_id) {
    console.error('User - no subscription ID provided')
    return Promise.resolve({ data: null })
  }
  return axios.get(get(server_path) + 'fastspring/get_subscription.php?s_id=' + s_id, { withCredentials: true })
    .catch(error => {
      console.error('Error fetching subscription details:', error)
      return { data: null }
    })
}

// Get user subscription management link from the server
function getUserAccountManagementLink(customer_id) {
  console.log('User - getting account management link')
  if (!customer_id) {
    console.error('User - no customer ID provided for account management link')
    return Promise.resolve({ data: null })
  }
  return axios.get(get(server_path) + 'fastspring/get_account_management_link.php?account_id=' + customer_id, { withCredentials: true })
    .catch(error => {
      console.error('Error fetching account management link:', error)
      return { data: null }
    })
}

// Get user subscription related data
export async function getUserSubscriptionRelatedData() {
  // get account management link if account ID is available
  if (get(user_subscription) && get(user_subscription).account) {
    let account_management_link = await getUserAccountManagementLink(get(user_subscription).account)
    if (account_management_link && account_management_link.data) {
      console.log('User - received account management link:', account_management_link.data)
      user.set({...get(user), account_management_link: account_management_link.data})
    } else {
      console.log('User - no valid account management link received')
    }
  }

  // get upsell estimate only if user is pro, subscription is active and product is defined
  if (get(user_subscription).product.toLowerCase().includes('pro') && get(user_subscription).state === 'active' && get(user_subscription).id) {
    try {
      let yearly_upsell_estimate = await getSubscriptionChangeEstimate(get(user_subscription).id, 'wordmark-business-12-subscription', true)
        .catch(error => {
          console.error('Error fetching yearly upsell estimate:', error)
          return { data: null }
        })
      
      let monthly_upsell_estimate = await getSubscriptionChangeEstimate(get(user_subscription).id, 'wordmark-business-monthly-subscription', false)
        .catch(error => {
          console.error('Error fetching monthly upsell estimate:', error)
          return { data: null }
        })
      
      // update upsell stores with upsell estimate only if data is available
      if (yearly_upsell_estimate && yearly_upsell_estimate.data) {
        yearly_upsell_from_pro_to_business_estimate.set(yearly_upsell_estimate.data)
      }
      
      if (monthly_upsell_estimate && monthly_upsell_estimate.data) {
        monthly_upsell_from_pro_to_business_estimate.set(monthly_upsell_estimate.data)
      }
      
      console.log('User - Upsell estimates:', get(yearly_upsell_from_pro_to_business_estimate), get(monthly_upsell_from_pro_to_business_estimate))
    } catch (error) {
      console.error('Error processing upsell estimates:', error)
    }
  }
}

// Refresh user subscription details
export async function refreshUserSubscription(s_id) {
  if (!s_id) {
    console.error('User - no subscription ID provided for refresh')
    return
  }
  try {
    let subs_response = await getUserSubscription(s_id)
    if (subs_response && subs_response.data) {
      user_subscription.set(subs_response.data)
      setSubscriptionRelatedStores(subs_response.data)
    }
  } catch (error) {
    console.error('Error refreshing user subscription:', error)
  }
}

// Toggle smart categories on or off
export async function toggleSmartCategoriesOn(toggle) {
  try {
    let form_data = new FormData()
    const toggle_value = toggle ? 1 : 0
    form_data.append('ai_categories_on', toggle_value)
    let response = await axios.post(get(server_path) + 'user/toggle_ai_categories.php', form_data, { withCredentials: true })
    return response
  } catch (error) {
    console.error('Error toggling smart categories:', error)
    return { data: null, error }
  }
}

// Save user font count to the server
export async function saveUserFontCount(count) {
  try {
    console.log('User - saving font count')
    let form_data = new FormData()
    form_data.append('font_count', count)
    let response = await axios.post(get(server_path) + 'user/save_font_count.php', form_data, { withCredentials: true })
    return response
  } catch (error) {
    console.error('Error saving font count:', error)
    return { data: null, error }
  }
}

// Run routine to set user data and subscription details
export async function runUserDataRoutine(user_data) {
  // Get and set subscription if user has subscription_id
  // Override database valid until with subscription valid until
  console.log('User - running user data routine')

  if (!user_data || !user_data.subscription_id) {
    console.log('User - no subscription ID found')
  }
  
  try {
    let subs_response = user_data && user_data.subscription_id ? await getUserSubscription(user_data.subscription_id) : false
    
    if (subs_response && subs_response.data) {
      console.log('User - received subscription details', subs_response.data)
      user_subscription.set(subs_response.data)

      // override user valid until if available
      if (subs_response.data.nextDisplayISO8601) {
        user_data.valid_until = subs_response.data.nextDisplayISO8601
      }

      // Set subscription related stores
      setSubscriptionRelatedStores(subs_response.data)

    } else {
      console.log('User - no subscription response or invalid subscription')
    }
  } catch (error) {
    console.error('Error in user data routine:', error)
  }

  // Set user data in store if available
  if (user_data) {
    user.set(user_data)
    console.log('User - received user data')
    console.log(get(user))

    // Set user validity
    setUserValidity()
  } else {
    console.error('User - no valid user data received')
  }
}

// Set subscription related stores
function setSubscriptionRelatedStores(subscription_data) {
  // Set can_upgrade_from_pro_to_business if pro subscription is active or business subscription is deactivated
  can_upgrade_from_pro_to_business.set(
    (subscription_data.product 
    && subscription_data.product.toLowerCase().includes('pro'))
    ||
    (subscription_data.product 
    && subscription_data.product.toLowerCase().includes('business') 
    && subscription_data.state === 'deactivated')
  )
  console.log('User - can_upgrade_from_pro_to_business: ', get(can_upgrade_from_pro_to_business))

  // Set pro_subscription_deactivated
  pro_subscription_deactivated.set(subscription_data.product && subscription_data.product.toLowerCase().includes('pro') && subscription_data.state === 'deactivated')
  console.log('User - pro_subscription_deactivated: ', get(pro_subscription_deactivated))

  // Set business_subscription_deactivated
  business_subscription_deactivated.set(subscription_data.product && subscription_data.product.toLowerCase().includes('business') && subscription_data.state === 'deactivated')
  console.log('User - business_subscription_deactivated: ', get(business_subscription_deactivated))

  // Set business_subscription_active
  business_subscription_active.set(subscription_data.product && subscription_data.product.toLowerCase().includes('business') && subscription_data.state === 'active')
  console.log('User - business_subscription_active: ', get(business_subscription_active))
}

// Set user validity based on subscription and expiration
function setUserValidity() {
  // default state is already false
  console.log('User - checking user validity')
  
  // check if user exists
  if (get(user)) {
    // check if not expired
    if (!get(user).expired) {
      user_valid.set(true)
      console.log('User - is valid, yearly account active')
    }
  }
  
  // check if subscription exists
  if (get(user_subscription)) {
    // check subscription state
    if (get(user_subscription).state !== "deactivated") {
      user_valid.set(true)
      console.log('User - is valid, subscription active')
    }
    else {
      user_valid.set(false)
    }
  }

  if (!get(user_valid)) console.log('User - could NOT be validated')
  
}

// Map user selections to fonts in the detected fonts store
function mapUserSelectionsToFonts(selections_data) {
  return new Promise(resolve => {
    for (let i = 0; i < selections_data.length; i++) {
      const selection = selections_data[i]
      const selection_id = selection.id
      const selected_fonts = selection.font_names.split('|-|')

      for (let j = 0; j < selected_fonts.length; j++) {
        const selected_font = selected_fonts[j]
        let found_font = find(get(detected_fonts), (f) => f.postscript_name === selected_font)
        if (found_font) {
          if (!found_font.selections) { found_font.selections = [] }
          found_font.selections = [...found_font.selections, selection_id]
          found_font.saved = true //TODO get rid of this by using tags length instead
        }
      }
    }
    detected_fonts.set(get(detected_fonts))
    console.log('User - mapped saved selections to fonts')
    resolve(true)
  })
}

// Reload fonts in selections by resetting and fetching them
export async function reloadFontsInSelections() {
  // Reset detected font selections
  get(detected_fonts).forEach(f => f.selection = undefined)
  // Get and map selections to fonts
  let selections_response = await getUserSavedSelections()
  if (selections_response) {
    if (!!selections_response.data) {
      user_saved_selections.set(selections_response.data)
      await mapUserSelectionsToFonts(selections_response.data)
      console.log('User - reset all saved selections in fonts')
    } else {
      user_saved_selections.set(undefined)
      console.log("User - has no saved selections yet")
    }
  }
}

// Poll subscription ID for first time subscription purchase
let subscription_poll_timeout
export async function pollSubscriptionId() {

  let response = await checkSubscriptionId()
  let result = response.data
  
  // If someone is logged in, check if the subscription belongs to the current user
  // This mismatch can only happen if email somehow is not set with Fastspring, 
  // and the data is used in both modals to inform users about this
  if (get(user) && result) {
    const currentUserEmail = get(user)?.email
    const subscriptionUserEmail = result.email
    
    if (currentUserEmail && subscriptionUserEmail && currentUserEmail !== subscriptionUserEmail) {
      console.log('Subscription - email mismatch detected')
      // Store the mismatch info for the modal
      // TODO: This data is renewe and lost store in global store instead
      subscription_email_mismatch.set({
        current_email: currentUserEmail,
        subscription_email: subscriptionUserEmail
      })
    }
  }

  if (result && result.status === 'new user') {
    console.log('Subscription - new user, opening create password form')
    modals.close('activatingAccount')
    modals.open('setPassword')
  }
  else if (result && result.status === 'existing user') {
    console.log('Subscription - existing user, confirming purchase')
    
    // assign new subscription ID to current user
    user.set({...get(user), subscription_id: get(new_subscription_id)})
    
    // get/set user_subscription and set user_valid
    await runUserDataRoutine(get(user))

    // Get user subscription related data
    await getUserSubscriptionRelatedData()

    // open final thank you modal
    modals.close('activatingAccount')
    modals.open('afterRenew')
  }
  else {
    console.log('Subscription - subscription_id not in DB yet')
    subscription_poll_timeout = setTimeout(() => pollSubscriptionId(), 1000)
  }
}

// Check if subscription ID is in DB
function checkSubscriptionId() {
  console.log('Subscription - checking if subscription_id is in DB')
  return axios.get(get(server_path) + 'user/check_subscription_id.php?subscription_id=' + get(new_subscription_id), { withCredentials: true })
}

// Save user password and get user data
export function saveUserPassword(form_data = null) {
  console.log('Subscription - saving user password')
  return axios.post(get(server_path) + 'user/set_password.php?subscription_id=' + get(new_subscription_id), form_data, { withCredentials: true })
}

// Set up user-specific integrations (FastSpring, Bugsnag, etc.)
function setupUserIntegrations(userData, env) {
  // Set FastSpring session vars to prefill order form
  if (typeof window.fastspring !== "undefined") {
    const fs_session = {
      'reset': true,
      'paymentContact': {
        'email': userData.email,
        'firstName': userData.first_name,
        'lastName': userData.last_name
      }
    }
    window.fastspring.builder.push(fs_session)
    // set the current user's email as the customer_email tag to receive via webhook to extend the right account
    window.fastspring.builder.push({'tags': { "customer_email": userData.email }})
    console.log('User - FastSpring session vars and tags set')
  }

  // Set user data for Bugsnag
  if (typeof Bugsnag !== "undefined" && Bugsnag && typeof Bugsnag.setUser === "function") {
    Bugsnag.setUser(userData.id, userData.email, userData.first_name + ' ' + userData.last_name)
    console.log('User - Bugsnag user set')
  }
}